import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { fmt, fmtPlain, parseMoney } from '../lib/money.js';
import { monthName, parts, toMonth } from '../lib/calendar.js';

/* ── Icons ─────────────────────────────────────────────── */
const PATHS = {
  check: 'M4 10.5l4 4 8-9',
  alert: 'M10 3l8 14H2L10 3zm0 5v4m0 2.5v.5',
  info: 'M10 2.5a7.5 7.5 0 100 15 7.5 7.5 0 000-15zM10 9v5m0-7.5V7',
  undo: 'M7 5L3 9l4 4M3 9h9a5 5 0 010 10h-2',
  redo: 'M13 5l4 4-4 4M17 9H8a5 5 0 000 10h2',
  plus: 'M10 4v12M4 10h12',
  trash: 'M4 6h12M8 6V4h4v2m-6 0l1 10h6l1-10',
  edit: 'M13.5 3.5l3 3L7 16H4v-3l9.5-9.5z',
  close: 'M5 5l10 10M15 5L5 15',
  download: 'M10 3v10m-4-4l4 4 4-4M4 16h12',
  upload: 'M10 14V4m-4 4l4-4 4 4M4 16h12',
  link: 'M8.5 11.5l3-3M7 9L5.5 10.5a3 3 0 004.2 4.2L11 13m-2-6l1.5-1.5a3 3 0 014.2 4.2L13 11',
  refresh: 'M16 10a6 6 0 11-1.8-4.3M16 3v4h-4',
  chevronR: 'M8 5l5 5-5 5',
  chevronL: 'M12 5l-5 5 5 5',
  chevronD: 'M5 8l5 5 5-5',
  calendar: 'M4 5h12v11H4zM4 8h12M7 3v3m6-3v3',
  user: 'M10 10a3.5 3.5 0 100-7 3.5 3.5 0 000 7zm-6 7a6 6 0 0112 0',
  sheet: 'M4 3h12v14H4zM4 7h12M4 11h12M8 7v10',
  logout: 'M8 4H4v12h4m4-9l3 3-3 3m3-3H8',
  piggy: 'M4 10a6 5 0 1012 0 6 5 0 00-12 0zm12 0h1.5M7 15l-.5 2m7-2l.5 2M13 8h.01',
};
export function Icon({ name, size = 16, className = '', title }) {
  return (
    <svg className={`icon ${className}`} width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined}>
      {title && <title>{title}</title>}
      <path d={PATHS[name] || PATHS.info} />
    </svg>
  );
}

/* ── Money ─────────────────────────────────────────────── */
export function Money({ value, sign = false, color = false, className = '', blankZero = false }) {
  if (value === null || value === undefined || Number.isNaN(value)) return <span className={`num muted ${className}`}>—</span>;
  const cls = color ? (value > 0.004 ? 'pos' : value < -0.004 ? 'neg' : '') : '';
  return <span className={`num ${cls} ${className}`}>{fmt(value, { sign, blankZero })}</span>;
}

// Text typed into a money box and what it means. Accepts "1.234,56", "1234.56", "1500 - 23,5"…
export function useMoneyText(initial, opts = {}) {
  const [text, setText] = useState(() => (initial === null || initial === undefined ? '' : fmtPlain(initial)));
  const parsed = useMemo(() => parseMoney(text, { allowEmpty: true, ...opts }), [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return { text, setText, parsed, reset: (v) => setText(v === null || v === undefined ? '' : fmtPlain(v)) };
}

export function MoneyInput({ value, onChange, parsed, placeholder, invalid, autoFocus, id, ariaLabel, onKeyDown, disabled, suffix = '€' }) {
  const isExpr = /[+*/×÷x()=]|\d\s*-\s*\d/.test(value || '');
  return (
    <div className="input-wrap">
      {isExpr && parsed?.ok && parsed.value !== null && <span className="preview num" aria-live="polite">= {fmt(parsed.value)}</span>}
      <input
        id={id}
        className={`input money ${invalid || (parsed && !parsed.ok) ? 'invalid' : ''}`}
        inputMode="decimal"
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        aria-invalid={parsed && !parsed.ok ? true : undefined}
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        disabled={disabled}
      />
      {suffix && <span className="suffix">{suffix}</span>}
    </div>
  );
}

// A label with its input. Use `group` when the field holds several controls (lists, toggles, month pickers):
// a <label> may only wrap one control.
export function Field({ label, hint, error, children, htmlFor, group = false }) {
  const Tag = group ? 'div' : 'label';
  return (
    <Tag className="field" htmlFor={group ? undefined : htmlFor} role={group ? 'group' : undefined} aria-label={group && typeof label === 'string' && label.trim() ? label : undefined}>
      {label && <span className="lbl">{label}</span>}
      {children}
      {error ? <span className="err" role="alert">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </Tag>
  );
}

// A labelled money box bound to a text state from useMoneyText
export function MoneyField({ label, hint, m, placeholder, autoFocus, disabled, error }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error || (!m.parsed.ok ? m.parsed.error : null)} htmlFor={id}>
      <MoneyInput id={id} ariaLabel={typeof label === 'string' ? label : undefined} value={m.text} onChange={m.setText} parsed={m.parsed} placeholder={placeholder} autoFocus={autoFocus} disabled={disabled} />
    </Field>
  );
}

export function Toggle({ checked, onChange, label, disabled }) {
  return (
    <label className="toggle">
      <input type="checkbox" role="switch" checked={!!checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      <span>{label}</span>
    </label>
  );
}

export function Seg({ value, options, onChange, ariaLabel }) {
  return (
    <div className="seg" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/* ── Months ────────────────────────────────────────────── */
export function MonthSelect({ value, onChange, fromYear = 2020, toYear = 2045, allowEmpty = false, emptyLabel = 'None', ariaLabel = 'Month' }) {
  const [y, m] = value ? parts(value) : [null, null];
  const years = [];
  for (let i = fromYear; i <= toYear; i++) years.push(i);
  if (y && !years.includes(y)) years.push(y);
  return (
    <div className="row" style={{ gap: 6 }}>
      <select className="input" aria-label={`${ariaLabel}: month`} value={m || ''} onChange={(e) => {
        const nm = Number(e.target.value);
        if (!nm) { if (allowEmpty) onChange(null); return; }
        onChange(toMonth(y || new Date().getFullYear(), nm));
      }}>
        {(allowEmpty || !value) && <option value="">{allowEmpty ? emptyLabel : '—'}</option>}
        {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{monthName(i + 1)}</option>)}
      </select>
      <select className="input" style={{ maxWidth: 96 }} aria-label={`${ariaLabel}: year`} value={y || ''} disabled={!value} onChange={(e) => onChange(toMonth(Number(e.target.value), m || 1))}>
        {!value && <option value="">—</option>}
        {years.sort((a, b) => a - b).map((yy) => <option key={yy} value={yy}>{yy}</option>)}
      </select>
    </div>
  );
}

/* ── Dialogs ───────────────────────────────────────────── */
export function Dialog({ title, onClose, children, footer, wide = false, dirty = false, labelledBy }) {
  const ref = useRef(null);
  const titleId = useId();
  const [confirming, setConfirming] = useState(false);
  const tryClose = () => {
    if (dirty) setConfirming(true);
    else onClose();
  };
  useEffect(() => {
    const prev = document.activeElement;
    const el = ref.current;
    const first = el?.querySelector('[autofocus], input, select, textarea, button:not(.dialog-close)');
    (first || el)?.focus?.();
    return () => { if (prev && prev.focus) prev.focus(); };
  }, []);
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); tryClose(); }
    if (e.key === 'Tab') {
      const items = [...ref.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((x) => !x.disabled && x.offsetParent !== null);
      if (!items.length) return;
      const firstItem = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstItem) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); firstItem.focus(); }
    }
  };
  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) tryClose(); }}>
      <div className={`dialog ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy || titleId} ref={ref} tabIndex={-1} onKeyDown={onKeyDown}>
        <div className="dialog-head">
          <h2 id={titleId}>{title}</h2>
          <span className="spacer" />
          <button type="button" className="btn ghost icon dialog-close" aria-label="Close" onClick={tryClose}><Icon name="close" /></button>
        </div>
        <div className="dialog-body">{children}</div>
        {footer && <div className="dialog-foot">{footer}</div>}
        {confirming && (
          <ConfirmDialog
            title="Discard changes?"
            message="What you typed here hasn't been saved."
            confirmLabel="Discard"
            danger
            onCancel={() => setConfirming(false)}
            onConfirm={() => { setConfirming(false); onClose(); }}
          />
        )}
      </div>
    </div>
  );
}

export function ConfirmDialog({ title, message, confirmLabel = 'OK', cancelLabel = 'Cancel', danger = false, typeToConfirm = null, onConfirm, onCancel, children }) {
  const [typed, setTyped] = useState('');
  const ok = !typeToConfirm || typed.trim() === typeToConfirm;
  return (
    <Dialog title={title} onClose={onCancel} footer={(
      <>
        <span className="spacer" />
        <button type="button" className="btn" onClick={onCancel}>{cancelLabel}</button>
        <button type="button" className={`btn ${danger ? 'danger solid' : 'primary'}`} disabled={!ok} onClick={onConfirm}>{confirmLabel}</button>
      </>
    )}>
      {message && <p className="dim">{message}</p>}
      {children}
      {typeToConfirm && (
        <Field label={`Type ${typeToConfirm} to confirm`}>
          <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
        </Field>
      )}
    </Dialog>
  );
}

export function StatusBadge({ status, source }) {
  if (source === 'sheet' && status === 'confirmed') return <span className="badge sheet">From spreadsheet</span>;
  const label = { pending: 'To review', confirmed: 'Confirmed', upcoming: 'Upcoming', partial: 'Partly done', empty: '—' }[status] || status;
  return <span className={`badge ${status}`}>{status === 'confirmed' && <Icon name="check" size={11} />}{label}</span>;
}

export function PersonDot({ color, size = 10 }) {
  return <span className="swatch" style={{ background: color, width: size, height: size, borderRadius: '50%' }} aria-hidden="true" />;
}

export function Card({ title, actions, children, className = '' }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <div className="card-head">
          {title && <h2>{title}</h2>}
          <span className="spacer" />
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

// Download bytes or text as a file
export function download(name, data, type = 'application/octet-stream') {
  const blob = data instanceof Blob ? data : new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// The text of a chosen file (with a fallback for browsers without File.text)
export function readFileText(file) {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("Couldn't read the file"));
    r.readAsText(file);
  });
}
